// src/components/conversation/Conversation.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode"; // Import JWT decoder
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  CardActionArea, 
  Grid, 
  Button, 
  IconButton,
  Skeleton,
  Dialog,
  Tab,
  Tabs,
  Avatar,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Tooltip
} from '@mui/material';
import conversationAPI from '../../conversationApi'; 
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';
import LanguageIcon from '@mui/icons-material/Language';
import SchoolIcon from '@mui/icons-material/School';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TranslateIcon from '@mui/icons-material/Translate';

import ThemeProvider from './ThemeProvider';
import ConversationApp from './ConversationApp';

// Language code to flag mapping for UI display
const languageFlags = {
  'en': 'gb',
  'es': 'es',
  'fr': 'fr',
  'de': 'de',
  'it': 'it',
  'ja': 'jp',
  'ko': 'kr',
  'zh': 'cn',
  'ru': 'ru',
  'pt': 'pt',
  'ar': 'sa',
  'hi': 'in'
};

// Language code to native and display name mapping
const languageNames = {
  'en': { display: 'English', native: 'English' },
  'es': { display: 'Spanish', native: 'Español' },
  'fr': { display: 'French', native: 'Français' },
  'de': { display: 'German', native: 'Deutsch' },
  'it': { display: 'Italian', native: 'Italiano' },
  'ja': { display: 'Japanese', native: '日本語' },
  'ko': { display: 'Korean', native: '한국어' },
  'zh': { display: 'Chinese', native: '中文' },
  'ru': { display: 'Russian', native: 'Русский' },
  'pt': { display: 'Portuguese', native: 'Português' },
  'ar': { display: 'Arabic', native: 'العربية' },
  'hi': { display: 'Hindi', native: 'हिन्दी' }
};

// Difficulty level to color mapping
const difficultyColors = {
  'beginner': {
    bg: '#e3f2fd',
    text: '#1565c0'
  },
  'intermediate': {
    bg: '#fff8e1',
    text: '#f57f17'
  },
  'advanced': {
    bg: '#fbe9e7',
    text: '#d84315'
  }
};

// Difficulty level icons for visual cue
const difficultyIcons = {
  'beginner': '●',
  'intermediate': '●●',
  'advanced': '●●●'
};

const Conversation = ({ token }) => {
  const { language } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Extract user ID from JWT token instead of using hardcoded value
  const [userId, setUserId] = useState(null);
  
  // Effect to decode JWT and extract userId
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserId(decoded.user._id);
      } catch (error) {
        console.error('Error decoding JWT token:', error);
        // If token is invalid, redirect to login
        navigate('/login', { replace: true });
      }
    }
  }, [token, navigate]);
  
  const [view, setView] = useState('templates'); // 'templates', 'history', 'conversation'
  const [categories, setCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [categoryTopics, setCategoryTopics] = useState({});

  // Effect to handle language change
  useEffect(() => {
    // If language is invalid, redirect to English
    if (!language || !languageNames[language]) {
      navigate('/chat/en', { replace: true });
    }
  }, [language, navigate]);

  // Load available languages
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        // In a real app, we would fetch this from the server
        // For now, we'll use our predefined list
        const languages = Object.keys(languageNames);
        setAvailableLanguages(languages);
      } catch (err) {
        console.error('Error loading languages:', err);
      }
    };
    
    loadLanguages();
  }, []);

  // Load conversation categories and templates
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        
        // Fetch all categories for this language
        const categoriesResponse = await conversationAPI.getCategories({ language });
        setCategories(categoriesResponse.data);
        
        // If there are categories, expand the first one by default
        if (categoriesResponse.data.length > 0) {
          setExpandedCategory(categoriesResponse.data[0]._id);
          
          // Fetch topics for the first category
          await loadCategoryTopics(categoriesResponse.data[0]._id);
        }
      } catch (err) {
        console.error('Error loading categories:', err);
        setError('Failed to load conversation categories');
      } finally {
        setLoading(false);
      }
    };
    
    if (view === 'templates' && language) {
      loadCategories();
    }
  }, [view, language]);

  // Load conversation history
  useEffect(() => {
    const loadHistory = async () => {
      if (!userId) return; // Don't attempt to load history without a userId
      
      try {
        setLoading(true);
        const response = await conversationAPI.getUserSessions(userId);
        
        // Filter history by selected language if needed
        const filteredHistory = language 
          ? response.data.filter(session => session.language === language)
          : response.data;
          
        setHistory(filteredHistory);
      } catch (err) {
        console.error('Error loading history:', err);
        setError('Failed to load conversation history');
      } finally {
        setLoading(false);
      }
    };
    
    if (view === 'history' && userId) {
      loadHistory();
    }
  }, [view, language, userId]);

  // Load topics for a specific category
  const loadCategoryTopics = async (categoryId) => {
    try {
      const response = await conversationAPI.getTemplatesByCategory(categoryId, { language });
      
      setCategoryTopics(prev => ({
        ...prev,
        [categoryId]: response.data
      }));
      
      return response.data;
    } catch (err) {
      console.error(`Error loading topics for category ${categoryId}:`, err);
      return null;
    }
  };

  // Handle category accordion expansion
  const handleCategoryExpand = async (categoryId) => {
    // If we're collapsing the current category
    if (expandedCategory === categoryId) {
      setExpandedCategory(null);
      return;
    }
    
    setExpandedCategory(categoryId);
    
    // Load topics for this category if not already loaded
    if (!categoryTopics[categoryId]) {
      await loadCategoryTopics(categoryId);
    }
  };

  // Handle language change
  const handleLanguageChange = (newLanguage) => {
    navigate(`/chat/${newLanguage}`);
  };

  // Handle starting a new conversation
  const handleStartConversation = (template) => {
    setSelectedTemplate(template._id);
    setView('conversation');
  };

  // Handle continuing an existing conversation
  const handleContinueConversation = (session) => {
    setSelectedSession(session._id);
    setView('conversation');
  };

  // Handle returning to templates or history view
  const handleBackToMain = () => {
    setSelectedTemplate(null);
    setSelectedSession(null);
    setView('templates');
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Generate skeleton loaders for categories
  const renderCategorySkeletons = () => {
    return Array(3).fill().map((_, index) => (
      <Accordion key={`category-skeleton-${index}`} disabled>
        <AccordionSummary>
          <Skeleton variant="rectangular" height={28} width="60%" />
        </AccordionSummary>
      </Accordion>
    ));
  };

  // Render topic skeletons within a category
  const renderTopicSkeletons = () => {
    return Array(4).fill().map((_, index) => (
      <Grid item xs={12} sm={6} md={4} key={`topic-skeleton-${index}`}>
        <Card sx={{ height: '100%', p: 0.5 }}>
          <Skeleton variant="rectangular" height={30} width="60%" sx={{ mb: 1, ml: 2, mt: 2 }} />
          <Skeleton variant="rectangular" height={20} width="80%" sx={{ mb: 1, ml: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mx: 2, mb: 2, mt: 2 }}>
            <Skeleton variant="rectangular" height={24} width={60} />
          </Box>
        </Card>
      </Grid>
    ));
  };

  // History loading skeletons
  const renderHistorySkeletons = () => {
    return Array(5).fill().map((_, index) => (
      <Grid item xs={12} key={`history-skeleton-${index}`}>
        <Card sx={{ p: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2 }}>
            <Box>
              <Skeleton variant="rectangular" height={24} width={200} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={20} width={300} />
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Skeleton variant="rectangular" height={24} width={80} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={20} width={100} />
            </Box>
          </Box>
        </Card>
      </Grid>
    ));
  };

  // Loading indicator
  if (loading && (view === 'templates' || view === 'history')) {
    return (
      <ThemeProvider>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* App header with language switcher */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SchoolIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
              <Typography variant="h5" component="h1" fontWeight="bold" color="primary.dark">
                {view === 'templates' ? 'Language Practice' : 'Conversation History'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box>
                <Tooltip title="Change language">
                  <Button
                    variant="outlined"
                    startIcon={<TranslateIcon />}
                    disabled
                  >
                    <Skeleton variant="rectangular" width={80} height={24} />
                  </Button>
                </Tooltip>
              </Box>
              
              <Button
                variant={view === 'templates' ? 'outlined' : 'contained'}
                startIcon={view === 'templates' ? <HistoryIcon /> : <AddIcon />}
                onClick={() => setView(view === 'templates' ? 'history' : 'templates')}
                disableElevation
                size="large"
              >
                {view === 'templates' ? 'View History' : 'New Conversation'}
              </Button>
            </Box>
          </Box>
          
          {view === 'templates' && (
            <>
              {renderCategorySkeletons()}
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={3}>
                  {renderTopicSkeletons()}
                </Grid>
              </Box>
            </>
          )}
          
          {view === 'history' && (
            <Grid container spacing={2}>
              {renderHistorySkeletons()}
            </Grid>
          )}
        </Container>
      </ThemeProvider>
    );
  }

  // Error display
  if (error && (view === 'templates' || view === 'history')) {
    return (
      <ThemeProvider>
        <Container maxWidth="sm" sx={{ pt: 4 }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => window.location.reload()}
            sx={{ mt: 2 }}
            disableElevation
          >
            Retry
          </Button>
        </Container>
      </ThemeProvider>
    );
  }

  // Conversation view
  if (view === 'conversation' && userId) { // Only render when userId is available
    return (
      <ThemeProvider>
        <Dialog
          fullScreen
          open={true}
          onClose={handleBackToMain}
        >
          <ConversationApp
            templateId={selectedTemplate}
            sessionId={selectedSession}
            userId={userId}
            userLanguage={language}
            onBack={handleBackToMain}
          />
        </Dialog>
      </ThemeProvider>
    );
  }

  // Templates & History view
  return (
    <ThemeProvider>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* App header with language switcher */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <SchoolIcon color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
            <Typography variant="h5" component="h1" fontWeight="bold" color="primary.dark">
              {view === 'templates' ? 'Language Practice' : 'Conversation History'}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Language selector button */}
            <Box>
              <Tooltip title="Change language">
                <Button
                  variant="outlined"
                  startIcon={
                    <Avatar
                      src={`https://flagcdn.com/w20/${languageFlags[language] || 'un'}.png`}
                      alt={languageNames[language]?.display || 'Language'}
                      sx={{ width: 20, height: 20 }}
                    />
                  }
                  onClick={(e) => {
                    // Open language selector menu
                    // This would open a menu with language options
                    // For simplicity, we'll just go to the next language in our list
                    const currentIndex = availableLanguages.indexOf(language);
                    const nextIndex = (currentIndex + 1) % availableLanguages.length;
                    handleLanguageChange(availableLanguages[nextIndex]);
                  }}
                >
                  {languageNames[language]?.native || language}
                </Button>
              </Tooltip>
            </Box>
            
            <Button
              variant={view === 'templates' ? 'outlined' : 'contained'}
              startIcon={view === 'templates' ? <HistoryIcon /> : <AddIcon />}
              onClick={() => setView(view === 'templates' ? 'history' : 'templates')}
              disableElevation
              size="large"
            >
              {view === 'templates' ? 'View History' : 'New Conversation'}
            </Button>
          </Box>
        </Box>
        
        {/* Templates View with Categories */}
        {view === 'templates' && (
          <>
            {categories.length > 0 ? (
              <>
                {/* Category accordions */}
                {categories.map((category) => (
                  <Accordion
                    key={category._id}
                    expanded={expandedCategory === category._id}
                    onChange={() => handleCategoryExpand(category._id)}
                    sx={{
                      mb: 2,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:before': {
                        display: 'none',
                      },
                      '&.Mui-expanded': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                        borderColor: 'primary.main',
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        backgroundColor: expandedCategory === category._id ? 'primary.lighter' : 'transparent',
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        {category.name}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {categoryTopics[category._id] ? (
                        <Grid container spacing={3}>
                          {categoryTopics[category._id].topics.map((topic) => (
                            <Grid item xs={12} sm={6} md={4} key={topic.name}>
                              <Card 
                                elevation={0}
                                sx={{ 
                                  height: '100%', 
                                  display: 'flex', 
                                  flexDirection: 'column',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
                                    borderColor: 'primary.main'
                                  }
                                }}
                              >
                                <CardActionArea 
                                  sx={{ flexGrow: 1, p: 1 }}
                                  onClick={() => handleStartConversation(topic.templates[0])}
                                >
                                  <CardContent>
                                    <Typography 
                                      variant="subtitle1" 
                                      component="h2" 
                                      fontWeight="bold"
                                      color="primary.dark"
                                      gutterBottom
                                    >
                                      {topic.name}
                                    </Typography>
                                    
                                    {topic.description && (
                                      <Typography 
                                        variant="body2" 
                                        color="text.secondary" 
                                        sx={{ mb: 2 }}
                                      >
                                        {topic.description}
                                      </Typography>
                                    )}
                                    
                                    <Box 
                                      sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mt: 2
                                      }}
                                    >
                                      <Chip
                                        avatar={
                                          <Avatar
                                            sx={{
                                              bgcolor: difficultyColors[topic.difficulty]?.bg || '#e0e0e0',
                                              color: difficultyColors[topic.difficulty]?.text || '#616161',
                                              fontWeight: 'bold'
                                            }}
                                          >
                                            {difficultyIcons[topic.difficulty]}
                                          </Avatar>
                                        }
                                        label={topic.difficulty}
                                        size="small"
                                        sx={{ 
                                          bgcolor: difficultyColors[topic.difficulty]?.bg || '#e0e0e0',
                                          color: difficultyColors[topic.difficulty]?.text || '#616161',
                                          fontWeight: 500,
                                          textTransform: 'capitalize',
                                          borderRadius: 2
                                        }}
                                      />
                                    </Box>
                                  </CardContent>
                                </CardActionArea>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Grid container spacing={3}>
                          {renderTopicSkeletons()}
                        </Grid>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}
              </>
            ) : (
              <Box 
                sx={{ 
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 4
                }}
              >
                <LanguageIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No conversation topics available for {languageNames[language]?.display || language}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Try selecting a different language or check back later
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<TranslateIcon />}
                  onClick={() => {
                    // Switch to first available language that isn't current
                    const otherLanguage = availableLanguages.find(lang => lang !== language) || 'en';
                    handleLanguageChange(otherLanguage);
                  }}
                  sx={{ mt: 2 }}
                  disableElevation
                >
                  Try Another Language
                </Button>
              </Box>
            )}
          </>
        )}
        
        {/* History List */}
        {view === 'history' && (
          <Box>
            {history.length > 0 ? (
              <Grid container spacing={2}>
                {history.map((session) => (
                  <Grid item xs={12} key={session._id}>
                    <Card 
                      elevation={0}
                      sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                          borderColor: 'primary.main'
                        }
                      }}
                    >
                      <CardActionArea 
                        onClick={() => handleContinueConversation(session)}
                      >
                        <CardContent sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 3
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Avatar 
                              src={`https://flagcdn.com/w40/${languageFlags[session.language] || 'un'}.png`}
                              alt={session.language}
                              variant="rounded"
                              sx={{ mr: 2, width: 36, height: 36 }}
                            />
                            
                            <Box>
                              <Typography 
                                variant="subtitle1" 
                                component="h2"
                                color="primary.dark"
                                fontWeight="bold"
                              >
                                {session.title}
                              </Typography>
                              
                              <Typography variant="body2" color="text.secondary">
                                {session.goal}
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Box sx={{ textAlign: 'right' }}>
                            <Chip
                              label={session.completed ? 'Completed' : 'In Progress'}
                              color={session.completed ? 'success' : 'primary'}
                              variant={session.completed ? 'filled' : 'outlined'}
                              size="small"
                              sx={{ 
                                mb: 1,
                                fontWeight: 500
                              }}
                            />
                            
                            <Typography 
                              variant="caption" 
                              display="block" 
                              color="text.secondary"
                              sx={{ fontWeight: 500 }}
                            >
                              {formatDate(session.lastUpdatedAt)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box 
                sx={{ 
                  textAlign: 'center',
                  py: 8,
                  px: 2,
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 4
                }}
              >
                <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No conversation history yet
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Start a new conversation to practice your {languageNames[language]?.display || language} skills
                </Typography>
                <Button 
                  variant="contained" 
                  startIcon={<AddIcon />}
                  onClick={() => setView('templates')}
                  sx={{ mt: 2 }}
                  disableElevation
                  size="large"
                >
                  Start a Conversation
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Container>
    </ThemeProvider>
  );
};

export default Conversation;